import React from "react";
import { Hooks, Util, XtLocale } from "@xt/xtbase";
import useSocialMediaLinks from "../hooks/useSocialMediaLinks";
import { getHelpCenterLink } from "../utils/helpCenterLink";
import { navPublicAssetUrl } from "../utils/navPublicAssetUrl";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { getUrl } = Util;

function tr(t, key, fallback) {
  if (!t || !key) return fallback;
  const value = t(key);
  if (!value || value === key) return fallback;

  const legacyValues = {
    "footer.sitePrivacyPolicy": ["Privacy Terms"],
    "footer.siteTermsOfService": ["User Agreement"],
    "footer.siteReferralCommission": ["Refer Friends", "Referral"],
    "footer.PrivacyTerms": ["Privacy Terms"],
    "footer.userAgreement": ["User Agreement"],
    "footer.Referral": ["Refer Friends", "Referral"],
  };

  if (legacyValues[key]?.includes(value)) return fallback;
  return value;
}

function getLegalDocLink(doc) {
  const articles = {
    agreement: "5158356356894",
    privacy: "5158509865246",
    risk: "5158511512862",
    cookie: "5525331953694",
  };
  return `https://spikex.zendesk.com/hc/${XtLocale.getZenLan()}/articles/${articles[doc] || articles.agreement}`;
}

function getAboutUsCategoryLink() {
  return `https://spikex.zendesk.com/hc/${XtLocale.getZenLan()}/categories/5158506228254-About-Us`;
}

const CONTACT_EMAIL = "marketing@spikex.com";

const MSB_REGISTRATION_PDF =
  "https://a.spikex.com/license/msb-Innovix-Technologies-Limited_registration.pdf";

function isZendeskHref(href) {
  return typeof href === "string" && href.startsWith("https://spikex.zendesk.com");
}

/**
 * PC Site Footer — 对齐 spikex-ai-web SiteFooter。
 * Community 与顶部 Help Center 共用 useSocialMediaLinks（整页只请求一次）。
 * 本地联调，先不 publish。
 */
export default function SiteFooter() {
  const t = useTranslation();
  const socialLinks = useSocialMediaLinks();

  const groups = [
    {
      title: tr(t, "footer.siteProducts", "Products"),
      links: [
        { label: tr(t, "header.markets", "Markets"), href: getUrl("/market") },
        { label: tr(t, "header.trade", "Trade"), href: getUrl("/futures/contract/spot") },
        { label: tr(t, "footer.siteAiLab", "AI Lab"), href: getUrl("/aicopy") },
        { label: tr(t, "footer.siteRewards", "Rewards"), href: getUrl("/referral") },
      ],
    },
    {
      title: tr(t, "footer.siteCompliance", "Legal & Compliance"),
      links: [
        { label: tr(t, "footer.siteTermsOfService", "Terms of Service"), href: getLegalDocLink("agreement") },
        { label: tr(t, "footer.sitePrivacyPolicy", "Privacy Policy"), href: getLegalDocLink("privacy") },
        { label: tr(t, "footer.siteRisk", "Risk Warning"), href: getLegalDocLink("risk") },
        { label: tr(t, "footer.siteMsb", "MSB Registration"), href: MSB_REGISTRATION_PDF, newTab: true },
      ],
    },
    {
      title: tr(t, "footer.siteEarn", "Earn"),
      links: [
        { label: tr(t, "footer.siteRewards", "Rewards"), href: getUrl("/referral") },
        { label: tr(t, "footer.siteTasks", "Tasks"), href: getUrl("/activity/reward-hub") },
        { label: tr(t, "footer.siteVouchers", "Vouchers"), href: getUrl("/activity/voucher") },
      ],
    },
    {
      title: tr(t, "footer.Company", "Company"),
      links: [
        { label: tr(t, "footer.aboutUs", "About Us"), href: getAboutUsCategoryLink() },
        { label: tr(t, "footer.officialAnn", "Announcements"), href: getUrl("/notification") },
      ],
    },
    {
      title: tr(t, "footer.Support", "Support"),
      links: [
        { label: tr(t, "footer.siteHelp", "Help Center"), href: getHelpCenterLink() },
        { label: tr(t, "footer.feeRate", "Fees"), href: getUrl("/rate") },
        { label: tr(t, "footer.contactUs", "Contact Us"), href: `mailto:${CONTACT_EMAIL}` },
      ],
    },
  ];

  const legalLinks = [
    { label: tr(t, "footer.sitePrivacyPolicy", "Privacy Policy"), href: getLegalDocLink("privacy") },
    { label: tr(t, "footer.siteTermsOfService", "Terms of Service"), href: getLegalDocLink("agreement") },
    { label: tr(t, "footer.siteCookie", "Cookie Policy"), href: getLegalDocLink("cookie") },
  ];

  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.glow} aria-hidden />
      <div className={styles.inner}>
        <div className={styles.grid}>
          <div className={styles.brandCol}>
            <a href={getUrl("/")} className={styles.logo} aria-label="SpikeX home">
              <img
                src={navPublicAssetUrl("brand/logo-mark.png")}
                alt=""
                width={30}
                height={30}
                className={styles.logoMark}
                draggable={false}
              />
              <span className={styles.logoWordmark}>
                Spike<span className={styles.logoAccent}>X</span>
              </span>
            </a>
            <p className={styles.tagline}>
              {tr(
                t,
                "footer.siteTagline",
                "The AI-Driven Crypto, Forex & Futures Trading Platform."
              )}
            </p>
            {socialLinks.length > 0 ? (
              <div className={styles.community}>
                <p className={styles.communityLabel}>{tr(t, "footer.siteCommunity", "Community")}</p>
                <div className={styles.socialRow}>
                  {socialLinks.map((item) => (
                    <a
                      key={item.key}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label}
                      className={styles.socialBtn}
                      title={item.label}
                    >
                      {item.iconUrl ? (
                        <img
                          src={item.iconUrl}
                          alt=""
                          width={17}
                          height={17}
                          className={styles.socialIconImg}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <span className={styles.socialFallback}>
                          {(item.label || "?").trim().charAt(0).toUpperCase()}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {groups.map((g) => (
            <div key={g.title} className={styles.col}>
              <h4 className={styles.colTitle}>{g.title}</h4>
              <ul className={styles.colList}>
                {g.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className={styles.colLink}
                      {...(l.newTab || isZendeskHref(l.href)
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : null)}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={styles.bottomBar}>
          <span>
            © {year} SpikeX. {tr(t, "footer.Rights", "All rights reserved.")}
          </span>
          <div className={styles.legalRow}>
            {legalLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className={styles.legalLink}
                {...(isZendeskHref(l.href) ? { target: "_blank", rel: "noopener noreferrer" } : null)}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
